import { useAudioStore } from '../state/audioStore.ts';
import { getOrCreateAudioContext, music, greyBed, subscribeSpeechActivity } from './audioEngine.ts';

type Status = 'off' | 'ready' | 'paused' | 'loading' | 'playing' | 'blocked' | 'failed' | 'ended' | 'disposed';
interface Preferences { musicEnabled: boolean; muted: boolean; musicVolume: number }
interface CoreSnapshot { enabled: boolean; muted: boolean; volume: number; unlocked: boolean; status: Status; reason: string }
interface MusicController {
  getSnapshot(): CoreSnapshot;
  getSource(): string;
  setSource(source: string, loop?: CatalogTrack['loop']): boolean;
  setRepeat(repeat: boolean): boolean;
  subscribe(fn: () => void): () => void;
  subscribeEnded(fn: (source: string) => void): () => void;
  setPreferences(value: Preferences): void;
  setHold(owner: string, held: boolean): void;
  toggleFromGesture(): void;
  requireGesture(): void;
  dispose(): void;
}
export type MusicRegion = 'HK' | 'CN' | 'JP';
export type MusicLoopMode = 'off' | 'all' | 'one';
export interface CatalogTrack {
  id: string; title: string; source: string; demo: boolean;
  loop?: { startSample: number; endSampleExclusive: number; sampleRate: number };
  downloads: Readonly<Partial<Record<'midi' | 'ogg' | 'mp3', string>>>;
}
interface CatalogSnapshot {
  region: MusicRegion; status: 'idle' | 'loading' | 'ready' | 'failed'; error: string;
  tracks: readonly CatalogTrack[]; selectedId: string | null; loopMode: MusicLoopMode;
}
interface CatalogPlayer {
  getSnapshot(): CatalogSnapshot;
  subscribe(fn: () => void): () => void;
  prepare(): Promise<unknown>;
  select(id: string): boolean;
  next(): boolean;
  setLoop(mode: MusicLoopMode): boolean;
  setRegion(region: MusicRegion): void;
  dispose(): void;
}
interface CatalogModule { createCatalogPlayer(owner: MusicController, options: { region: MusicRegion }): CatalogPlayer }
interface MusicModule { getDocumentMusicController(options: {
  getContext: typeof getOrCreateAudioContext;
  readPreferences: () => Preferences;
  writePreferences: (value: Preferences) => void;
  beforePlay: () => void;
}): MusicController }
export interface SunsetMusicSnapshot extends CoreSnapshot {
  ready: boolean; region: MusicRegion | null; catalogStatus: CatalogSnapshot['status']; catalogError: string;
  tracks: readonly CatalogTrack[]; selectedId: string | null; loopMode: MusicLoopMode;
}
const emptyTracks: readonly CatalogTrack[] = Object.freeze([]);
const initial: SunsetMusicSnapshot = Object.freeze({ enabled: false, muted: false, volume: 0.4, unlocked: false, status: 'off', reason: '', ready: false,
  region: 'HK', catalogStatus: 'idle', catalogError: '', tracks: emptyTracks, selectedId: null, loopMode: 'one' });
let snapshot = initial;
let controller: MusicController | null = null;
let preparing: Promise<void> | null = null;
let catalog: CatalogPlayer | null = null, catalogPreparing: Promise<void> | null = null, catalogUnsubscribe: (() => void) | null = null;
let catalogFailure = '', region: MusicRegion | null = 'HK';
let generation = 0;
let greyActive = false, speechActive = false, routeAllowed = false, pageDeparted = false;
let lastPath = '';
let cleanup: (() => void) | null = null;
const listeners = new Set<() => void>();
const readPreferences = (): Preferences => {
  const { musicEnabled, muted, musicVolume } = useAudioStore.getState();
  return { musicEnabled, muted, musicVolume };
};
function publish() {
  const currentCatalog = catalog?.getSnapshot();
  const listed = currentCatalog?.region === region ? currentCatalog : undefined;
  snapshot = controller ? Object.freeze({ ...controller.getSnapshot(), ready: true, region,
    catalogStatus: listed?.status || (catalogFailure ? 'failed' : 'idle'), catalogError: listed?.error || catalogFailure,
    tracks: listed?.tracks || emptyTracks, selectedId: listed?.selectedId || null, loopMode: listed?.loopMode || 'one' })
    : Object.freeze({ ...initial, region });
  for (const fn of listeners) fn();
  syncGreyBed();
}
function syncGreyBed() {
  const prefs = readPreferences();
  const permitted = greyActive && routeAllowed && snapshot.unlocked && prefs.musicEnabled && !prefs.muted && prefs.musicVolume > 0 &&
    !speechActive && !pageDeparted && snapshot.reason !== 'navigation' && !document.hidden && !matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (permitted) {
    greyBed.setMusicVolume(prefs.musicVolume);
    greyBed.setMusicMuted(false);
    greyBed.start();
  } else greyBed.stop(true);
}

/** Loads the small controller, never the MP3. A fresh document still needs a gesture. */
export function prepareSunsetMusic(): Promise<void> {
  if (preparing) return preparing;
  const token = ++generation;
  snapshot = Object.freeze({ ...initial, region, status: 'loading', reason: 'player' });
  for (const fn of listeners) fn();
  // Native same-origin URL: Vite otherwise adds ?import to public files and
  // rejects them as source modules during development/browser verification.
  const modulePath = `${globalThis.location?.origin || ''}/audio/sunset-music.js`;
  preparing = import(/* @vite-ignore */ modulePath).then((module: MusicModule) => {
    if (token !== generation) return;
    controller = module.getDocumentMusicController({
      getContext: getOrCreateAudioContext,
      readPreferences,
      writePreferences: value => useAudioStore.setState(value),
      beforePlay: () => { music.stop(true); greyBed.stop(true); }
    });
    controller.setHold('catalog-route', region !== 'HK');
    const unsubscribe = controller.subscribe(publish);
    const speechUnsubscribe = subscribeSpeechActivity(active => {
      speechActive = active;
      controller?.setHold('speech', active);
      syncGreyBed();
    });
    const visibility = () => syncGreyBed();
    const hide = () => { pageDeparted = true; greyBed.stop(true); };
    const show = () => { pageDeparted = false; syncGreyBed(); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    controller.setHold('route', !routeAllowed);
    controller.setHold('grey-hour', greyActive);
    controller.setHold('speech', speechActive);
    publish();
    cleanup = () => {
      unsubscribe(); speechUnsubscribe();
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
    };
  }).catch(() => {
    if (token !== generation) return;
    snapshot = Object.freeze({ ...initial, region, status: 'failed', reason: 'player' });
    for (const fn of listeners) fn();
    preparing = null;
  });
  return preparing;
}

export function syncSunsetPreferences() { controller?.setPreferences(readPreferences()); syncGreyBed(); }
const cnRoutes = new Set(['/shenzhen', '/nanjing', '/beijing', '/shanghai', '/xian', '/zhengzhou', '/luoyang']);
const jpRoutes = new Set(['/tokyo', '/fukuoka', '/nagoya', '/yokohama', '/osaka', '/kyoto']);
export function musicRegionForRoute(path: string): MusicRegion | null {
  if (path === '/planet' || path.startsWith('/planet/') || path === '/hk' || path === '/settings') return 'HK';
  if (cnRoutes.has(path)) return 'CN';
  return jpRoutes.has(path) ? 'JP' : null;
}
function catalogChanged() {
  controller?.setHold('catalog-route', region !== 'HK' && catalog?.getSnapshot().status !== 'ready');
  publish();
}
function disposeCatalog() {
  catalogUnsubscribe?.(); catalogUnsubscribe = null; catalog?.dispose(); catalog = null; catalogFailure = '';
}
/** Called when the music controls are shown, never by the global audio hook. */
export async function prepareSunsetMusicCatalog(): Promise<void> {
  if (!controller) await prepareSunsetMusic();
  if (!controller || !region || !routeAllowed) return;
  if (catalog) { await catalog.prepare(); return; }
  if (catalogPreparing) return catalogPreparing;
  const token = generation;
  const modulePath = `${globalThis.location?.origin || ''}/audio/catalog-player.js`;
  catalogPreparing = import(/* @vite-ignore */ modulePath).then(async (module: CatalogModule) => {
    if (token !== generation || !controller || !region || !routeAllowed) return;
    catalog = module.createCatalogPlayer(controller, { region });
    catalogUnsubscribe = catalog.subscribe(catalogChanged); catalogChanged();
    await catalog.prepare();
  }).catch(() => {
    if (token !== generation) return;
    catalogFailure = 'Music catalog could not load. Retry when ready.'; publish();
  }).finally(() => { if (token === generation) catalogPreparing = null; });
  return catalogPreparing;
}
export function selectSunsetMusicTrack(id: string) { return catalog?.select(id) || false; }
export function nextSunsetMusicTrack() { return catalog?.next() || false; }
export function setSunsetMusicLoop(mode: MusicLoopMode) { return catalog?.setLoop(mode) || false; }
export function setSunsetMusicRoute(path: string) {
  const nextRegion = musicRegionForRoute(path);
  routeAllowed = nextRegion !== null;
  if (path !== lastPath) { lastPath = path; controller?.requireGesture(); }
  controller?.setHold('route', !routeAllowed);
  if (nextRegion !== region) {
    // Hold before releasing a prior model so another region's source cannot
    // become eligible while its metadata is still loading.
    controller?.setHold('catalog-route', true);
    region = nextRegion; catalogFailure = '';
    if (region) catalog?.setRegion(region); else disposeCatalog();
  }
  controller?.setHold('catalog-route', region !== 'HK' && catalog?.getSnapshot().status !== 'ready');
  publish();
}
export function setGreyHourMusicActive(active: boolean) {
  greyActive = active;
  controller?.setHold('grey-hour', active);
  syncGreyBed();
}
export function toggleSunsetMusicFromGesture() {
  if (!controller) { void prepareSunsetMusic(); return; }
  if (region !== 'HK' && catalog?.getSnapshot().status !== 'ready') { void prepareSunsetMusicCatalog(); return; }
  // Unlock the existing shared context inside the activation event, including
  // the case where Grey Hour currently owns the music slot instead of the MP3.
  if (!snapshot.enabled || !snapshot.unlocked || snapshot.status === 'blocked' || snapshot.status === 'failed' || snapshot.status === 'ended') {
    try { const ctx = getOrCreateAudioContext(); if (ctx?.state === 'suspended') void ctx.resume().catch(() => controller?.requireGesture()); } catch { /* Controller exposes its retry state. */ }
  }
  controller.toggleFromGesture();
}
export function disposeSunsetMusic() {
  generation++; cleanup?.(); cleanup = null; pageDeparted = false;
  controller?.setHold('route', true); disposeCatalog(); catalogPreparing = null;
  controller?.dispose(); controller = null; preparing = null;
  greyBed.stop(true); music.stop(true); publish();
}
export const getSunsetMusicSnapshot = () => snapshot;
export const getServerSunsetMusicSnapshot = () => initial;
export function subscribeSunsetMusic(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
